import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

const BG =
  "https://images.unsplash.com/photo-1629909613654-28e377c37b09?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIGHLIGHTS = [
  "Profile, skills & clinical philosophy",
  "Sixteen fully photo-documented cases",
  "Implant, full-mouth, esthetic & maxillofacial work",
];

export const Portfolio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  const handleDownload = () => {
    toast.success("Preparing your clinical portfolio…");
    window.open(`${API}/portfolio/download`, "_blank");
  };

  return (
    <section
      id="portfolio"
      ref={ref}
      data-testid="portfolio-section"
      className="relative overflow-hidden bg-emerald grain-overlay"
    >
      <motion.img
        style={{ y }}
        src={BG}
        alt=""
        className="absolute inset-0 w-full h-[130%] object-cover opacity-[0.14]"
      />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-36 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <p className="text-champagne text-xs uppercase tracking-[0.3em] mb-6">
            Clinical Portfolio
          </p>
          <h2 className="font-display font-light text-4xl md:text-5xl lg:text-6xl tracking-tight text-ivory leading-[1.05]">
            A curated dossier of clinical work.
          </h2>
          <p className="mt-8 max-w-xl text-ivory/70 text-base md:text-lg leading-relaxed">
            The complete Clinical Vault — sixteen photo-documented cases spanning zygomatic and
            All-on-6 implant rehabilitation, full-mouth reconstruction, laminates, precision
            attachments and maxillofacial prosthetics. Prepared for patients and referring
            colleagues alike.
          </p>

          <ul className="mt-10 space-y-4">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-center gap-4 text-ivory/80">
                <span className="h-px w-8 bg-champagne" />
                <span className="text-sm md:text-base tracking-wide">{h}</span>
              </li>
            ))}
          </ul>

          <div className="mt-12 flex flex-wrap items-center gap-5">
            <button
              data-testid="portfolio-download-btn"
              onClick={handleDownload}
              className="inline-flex items-center gap-3 bg-champagne text-emerald px-8 py-4 uppercase tracking-[0.15em] text-sm hover:bg-champagne-light transition-colors duration-300"
            >
              <Download size={16} />
              Download Clinical Portfolio (PDF)
            </button>
          </div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm aspect-[3/4] bg-ivory p-10 flex flex-col justify-between shadow-[0_30px_60px_rgba(0,0,0,0.35)]"
          >
            <div>
              <FileText className="text-champagne-dark" size={34} />
              <p className="mt-8 font-display text-2xl text-emerald leading-tight">
                Dr. Priya H. Mishra
              </p>
              <p className="text-xs uppercase tracking-[0.25em] text-champagne-dark mt-2">
                Clinical Portfolio
              </p>
            </div>
            <div className="border-t border-emerald/15 pt-5">
              <p className="text-sm text-emerald/60">
                Prosthodontics · Implantology · Esthetics
              </p>
            </div>
            <div className="absolute -top-4 -right-4 w-full h-full border border-champagne/40 -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
